import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { PropertyListener } from '@vsolv/dev-kit/rx';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { IconName } from '@vsolv/vectors-ui/icons';
import { Policy } from '@wsphere/warranties/domain';
import { BehaviorSubject, combineLatest, shareReplay, switchMap, tap } from 'rxjs';
import { POLICY_DETAILS_CONFIG } from '../../config/policy-details.config';
import { PolicyService } from '../../services';

export interface PolicyDetails {
  title: string;
  friendlyTitle: string;

  tagline: string;
  description?: string;
  order?: number;
  carrier?: string;
  tags: string;
  prefix: string;
  icon: IconName;

  policyNumber: string;
  claimWaitingPeriod: number;
  requiresWarrantyActivation: boolean;
  allowMarkup: boolean;
  checkClaimEligibility: boolean;
  checkoutPlanDisplay: Policy.CheckoutPlanDisplay;
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-policy-details',
  templateUrl: './policy-details.component.html',
  standalone: false,
})
export class PolicyDetailsComponent {
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private toastSvc: ToastService,
    private policySvc: PolicyService,
    private formBuilder: FormBuilder
  ) {}

  @PropertyListener('policy') policy$ = new BehaviorSubject<Policy.Model | null>(null);
  @Input() policy: Policy.Model | null = null;

  @PropertyListener('editing') editing$ = new BehaviorSubject<boolean>(false);
  @Input() editing = false;

  @Input() value: PolicyDetails | null = null;

  @Output() closed = new EventEmitter();
  @Output() pageCompleted = new EventEmitter<PolicyDetails>();

  saving = false;

  canEditPrefix = false;

  readonly config = POLICY_DETAILS_CONFIG;

  draft = Policy.Status.DRAFT;

  form = this.formBuilder.group({
    title: ['', [Validators.required]],
    friendlyTitle: ['', [Validators.maxLength(50)]],

    tagline: ['', [Validators.required, Validators.maxLength(100)]],
    description: ['', []],
    order: [0, [Validators.min(0)]],
    carrier: ['', [Validators.maxLength(50)]],
    tags: ['', [Validators.pattern(/^\s*(?:[A-Za-z0-9]+(?:\s*,\s*[A-Za-z0-9]+)*)?\s*$/)]],
    prefix: ['', [Validators.required, Validators.pattern('[a-zA-Z][a-zA-Z ]*')]],
    icon: ['file-shield-02' as string | null, Validators.required],

    policyNumber: ['', [Validators.required]],
    claimWaitingPeriod: [0, [Validators.min(0)]],
    requiresWarrantyActivation: [false as boolean],
    allowMarkup: [false as boolean],
    checkClaimEligibility: [false as boolean],
    checkoutPlanDisplay: [Policy.CheckoutPlanDisplay.TERM_FIRST as Policy.CheckoutPlanDisplay],
  });

  readonly checkoutPlanDisplayOptions: { value: Policy.CheckoutPlanDisplay; label: string }[] = [
    { value: Policy.CheckoutPlanDisplay.TERM_FIRST, label: 'Term → Plan' },
    { value: Policy.CheckoutPlanDisplay.PLAN_FIRST, label: 'Plan → Term' },
  ];

  patchValue$ = combineLatest([this.policy$, this.editing$]).pipe(
    tap(([policy, editing]) => this.loadForm(policy, editing))
  );

  // Duplicated policies inherit the prefix; allow changing it only on a draft with no warranties/versions.
  prefixEditable$ = combineLatest([this.policy$, this.editing$]).pipe(
    switchMap(async ([policy, editing]) => {
      if (!editing || !policy) return false;
      if (policy.status !== Policy.Status.DRAFT) return false;
      if (policy.succeededById || policy.policyNumber.split('-').length > 2) return false;

      const counts = await this.policySvc.getWarrantyCounts(policy.id);
      const total = counts ? Object.values(counts).reduce((sum, count) => sum + (count ?? 0), 0) : 0;
      return total === 0;
    }),
    tap(editable => (this.canEditPrefix = editable)),
    shareReplay(1)
  );

  close() {
    // Reload from the current inputs instead of overwriting them: the dialog's [policy]/[editing]
    // bindings don't change between opens, so Angular would never set them back and the next Edit
    // would reopen in create mode with a blank form.
    this.reload();

    this.closed.emit();
  }

  /** Reload the form from the current inputs, dropping any unsaved edits. */
  reload() {
    this.form.reset();
    this.loadForm(this.policy, this.editing);
  }

  /** Edit mode shows the saved details; create mode suggests a policy number or restores the wizard's value. */
  private loadForm(policy: Policy.Model | null, editing: boolean) {
    if (!editing) {
      if (!this.value) {
        const year = new Date().getFullYear().toString();
        const random = Math.random().toString().slice(-4);

        this.form.patchValue({ policyNumber: year + random });
      } else {
        this.form.patchValue(this.value);
        this.form.markAsDirty();
      }
    } else {
      if (!policy) return;

      const policyNumber = policy.policyNumber.split('-');
      this.form.patchValue({
        title: policy.title,
        friendlyTitle: policy?.friendlyTitle,

        tagline: policy.tagline,
        description: policy.description,
        order: policy.order,
        carrier: policy.carrier ?? '',
        tags: policy.tags?.join(',') ?? '',
        prefix: policyNumber[0],
        icon: policy.icon ?? 'file-shield-02',

        policyNumber: policyNumber[1],
        claimWaitingPeriod: policy.claimWaitingPeriod,
        requiresWarrantyActivation: policy.requiresWarrantyActivation ?? false,
        allowMarkup: policy.allowMarkup ?? false,
        checkClaimEligibility: policy.checkClaimEligibility ?? false,
        checkoutPlanDisplay: policy.checkoutPlanDisplay ?? Policy.CheckoutPlanDisplay.TERM_FIRST,
      });
    }
  }

  pageComplete() {
    this.pageCompleted.emit({
      title: this.form.value.title || '',
      friendlyTitle: this.form.value.friendlyTitle || '',

      tagline: this.form.value.tagline || '',
      description: this.form.value.description || '',
      order: this.form.value.order || 0,
      carrier: this.form.value.carrier || '',
      tags: this.form.value.tags || '',
      prefix: this.form.value.prefix || '',
      icon: (this.form.value.icon as IconName) || 'file-shield-02',

      policyNumber: this.form.value.policyNumber || '',
      claimWaitingPeriod: this.form.value.claimWaitingPeriod || 0,
      requiresWarrantyActivation: !!this.form.value.requiresWarrantyActivation,
      allowMarkup: this.form.value.allowMarkup ?? false,
      checkClaimEligibility: this.form.value.checkClaimEligibility ?? false,
      checkoutPlanDisplay:
        (this.form.value.checkoutPlanDisplay as Policy.CheckoutPlanDisplay) ?? Policy.CheckoutPlanDisplay.TERM_FIRST,
    });
  }

  async save() {
    if (!this.policy) return;

    this.saving = true;
    const value = this.form.value;

    const body: Policy.UpdatePolicyRequest = {
      canCreateNewVersion: true,

      title: value.title || '',
      friendlyTitle: value.friendlyTitle || undefined,

      tagline: value.tagline || '',
      description: value.description || '',
      order: value.order || 0,
      carrier: value.carrier || '',
      icon: value.icon || 'file-shield-02',
      tags: value.tags || '',

      claimWaitingPeriod: value.claimWaitingPeriod || 0,
      requiresWarrantyActivation: value.requiresWarrantyActivation ?? false,
      allowMarkup: value.allowMarkup ?? false,
      checkClaimEligibility: value.checkClaimEligibility ?? false,
      checkoutPlanDisplay:
        (value.checkoutPlanDisplay as Policy.CheckoutPlanDisplay) ?? Policy.CheckoutPlanDisplay.TERM_FIRST,
    };

    // Only the prefix changes; keep the sequence and send when different.
    if (this.canEditPrefix && value.prefix) {
      const policyNumber = value.prefix.trim() + '-' + (value.policyNumber || '');
      if (policyNumber !== this.policy.policyNumber) body.policyNumber = policyNumber;
    }

    const policy = await this.policySvc.update(this.policy.id, body).catch(({ error }) => {
      this.toastSvc.show({
        type: 'error',
        title: 'Something went wrong',
        text: error.message,
      });

      this.closed.emit();
      this.saving = false;
    });

    if (policy) {
      if (policy.id === this.policy.id) {
        this.toastSvc.show({
          type: 'success',
          title: 'Updated policy',
          text: '<strong>' + value.title + '</strong> has been successfully updated.',
        });

        this.policySvc.refreshPolicy();
      } else {
        this.toastSvc.show({
          type: 'success',
          title: 'New policy version created',
          text: 'An updated version of <strong>' + value.title + '</strong> has been successfully created.',
        });

        this.policySvc.refreshPolicy(policy.id);
        this.policySvc.clearPlan();

        // Absolute: from the policies list the host route consumes no segments, so '../' escapes /policies
        this.navigateTo('/policies/' + policy.id);
      }

      this.saving = false;
      this.closed.emit();
    }
  }

  navigateTo(path: string) {
    this.router.navigate([`${path}`], { relativeTo: this.route });
  }
}
